'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ChevronDown, Loader2 } from 'lucide-react';
import { useActionState, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { login } from './actions';

const SSO_PROVIDERS = [{ id: 'microsoft', label: 'Microsoft' }] as const;

export default function LoginForm({ ssoEnabled }: { ssoEnabled: boolean }) {
  const [state, formAction, isPending] = useActionState(login, null);
  const [isSsoPending, setIsSsoPending] = useState(false);
  const searchParams = useSearchParams();
  const inviteToken = searchParams.get('invite');
  const ssoError = searchParams.has('sso_error');

  return (
    <div className='w-full max-w-md'>
      <div className='flex w-full flex-col items-center justify-center'>
        <div className='mb-12 flex justify-center'>
          <Image
            src='/makeoff-logo.png'
            alt='MAKEOFF Logo'
            width={300}
            height={100}
            priority
            className='mx-auto'
          />
        </div>

        <div className='w-full rounded-md bg-white p-8 shadow-sm'>
          <h2 className='mb-6 text-center text-2xl font-medium'>Login</h2>

          <div className='space-y-4'>
            {(state?.error || ssoError) && (
              <div className='text-red-500' data-testid='login-error-message'>
                {state?.error || 'Unable to sign in with Microsoft. Please try again.'}
              </div>
            )}

            <form
              className='space-y-4'
              action={formAction}
              key={state?.email || 'login-form'}
              data-testid='login-form'
            >
              <div className='space-y-2'>
                <Input
                  id='email'
                  name='email'
                  placeholder='Email'
                  type='email'
                  className='h-12'
                  required
                  disabled={isPending}
                  defaultValue={state?.email || ''}
                  data-testid='login-email-input'
                />
              </div>

              <div className='space-y-2'>
                <Input
                  id='password'
                  name='password'
                  placeholder='Password'
                  type='password'
                  className='h-12'
                  required
                  disabled={isPending}
                  data-testid='login-password-input'
                />
              </div>

              {inviteToken && <input type='hidden' name='inviteToken' value={inviteToken} />}

              <Button
                className='mt-4 h-12 w-full cursor-pointer'
                disabled={isPending}
                data-testid='login-submit-button'
              >
                Login
              </Button>

              <div className='mt-4 text-center'>
                <Link
                  href='/forgot'
                  className='text-sm text-gray-600 hover:underline'
                  data-testid='login-forgot-password-link'
                >
                  Forgot Password
                </Link>
              </div>
            </form>

            {ssoEnabled && (
              <>
                <div className='flex items-center gap-3 text-xs text-gray-500'>
                  <div className='h-px flex-1 bg-gray-200' />
                  <span>or</span>
                  <div className='h-px flex-1 bg-gray-200' />
                </div>
                <form
                  id='sso-form'
                  action='/auth/sso'
                  method='get'
                  onSubmit={() => setIsSsoPending(true)}
                  data-testid='sso-form'
                >
                  {inviteToken && <input type='hidden' name='invite' value={inviteToken} />}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type='button'
                        variant='outline'
                        className='h-12 w-full'
                        disabled={isSsoPending}
                      >
                        {isSsoPending ? (
                          <>
                            <Loader2 className='animate-spin' aria-hidden='true' />
                            Signing in...
                          </>
                        ) : (
                          <>
                            Sign in with SSO
                            <ChevronDown className='ml-auto' aria-hidden='true' />
                          </>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align='start'
                      className='w-[var(--radix-dropdown-menu-trigger-width)]'
                    >
                      {SSO_PROVIDERS.map((provider) => (
                        <DropdownMenuItem key={provider.id} asChild>
                          <button
                            type='submit'
                            form='sso-form'
                            name='provider'
                            value={provider.id}
                            className='w-full'
                          >
                            Continue with {provider.label}
                          </button>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </form>
              </>
            )}
          </div>
        </div>

        <div className='mt-6 w-full text-center'>
          <p className='text-gray-600'>
            Don&apos;t have an account?{' '}
            <Link
              href={`/register${inviteToken ? `?invite=${inviteToken}` : ''}`}
              className='text-primary font-medium hover:underline'
              data-testid='login-signup-link'
            >
              Sign Up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
